import axios from 'axios';
import { createApiRequestUrl, CommonResult, OK } from '../Common';
import { API_EXCEPTION_MSG } from '../../Common';

/**
 * 来店予約サマリ・一覧取得APIリクエスト
 */
export interface GetVisitSummaryApiRequest {
  visitSummaryInfoList: VisitSummaryInfo[];
}

/**
 * 来店予約サマリ・一覧取得APIレスポンス
 */
export interface GetVisitSummaryApiResponse {
  /** 来店予約一覧取得応答 */
  visitSummaryResultDto?: VisitSummaryResult;
  /** 来店予約明細 */
  visitReservationDetailsDto?: VisitReservationDetails[];
  /** 来店予約一覧取得結果 */
  visitReservationResultDto: VisitReservationResult;
  /** 処理結果情報 */
  result: CommonResult;
}

/**
 * 来店予約一覧取得要求
 */
interface VisitSummaryInfo {
  /** 代理店コード */
  agencyCd: string;
  /** 検索開始日付 */
  scheduleStartDate: string;
  /** 検索終了日付 */
  scheduleEndDate: string;
}

/**
 * 来店予約一覧取得応答
 */
export interface VisitSummaryResult {
  /** ReservationCount.Web */
  webReservationCount: number;
  /** ReservationCount.Web（うち当日） */
  webReservationDayCount: number;
  /** ReservationCount.クルー */
  crewReservationCount: number;
  /** ReservationCount.クルー（うち当日） */
  crewReservationDayCount: number;
  /** 事前準備.架電 */
  befCallDto: BefCall[];
  /** 事前準備.クルーヒアリング */
  hearingDto: Hearing[];
  /** 事前準備.照会 */
  refAgreeStatusDto: RefAgreeStatus[];
  /** LINEステータス */
  lineContactDtos: LineContact[];
}

/**
 * 来店予約一覧取得結果
 */
interface VisitReservationResult {
  /** 取得件数 */
  count: number;
}

/**
 * 来店予約明細DTO
 */
export interface VisitReservationDetails {
  /** 予約ＩＤ */
  scheduleId?: string;
  /** VERSIONNO */
  versionNo?: number;
  /** クルーコード */
  commonId?: string;
  /** GINIE_ID */
  ginieId?: string;
  /** 担当者氏名 */
  staffName?: string;
  /** 予約時間タイプ */
  timeTyp?: string;
  /** 予約開始日付 */
  scheduleStartDateDate?: string;
  /** 予約開始時刻 */
  scheduleStartDateTime?: string;
  /** 予約終了日付 */
  scheduleEndDateDate?: string;
  /** 予約終了時刻 */
  scheduleEndDateTime?: string;
  /** お客様名・姓 */
  customerLastNm?: string;
  /** お客様名・名 */
  customerFirstNm?: string;
  /** 枠消費区分 */
  blockTyp?: string;
  /** 初回経路区分 */
  firstRouteKbn?: string;
  /** 当日変更フラグ */
  todayChgFlg?: string;
  /** 来店呼び出し番号 */
  shopCallNo?: string;
  /** ステータス */
  statusCd?: string;
  /** 予約タイプ */
  scheduleTyp?: string;
  /** 顧客情報照会利用可能フラグ */
  customerRefAvailableFlg?: string;
  /** 事前架電 */
  befCall?: string;
  /** ヒアリング */
  hearing?: string;
  /** ヒアリング(WEB) */
  hearingWeb?: string;
  /** 事前の動画視聴有無フラグ */
  watchVideoFlg?: string;
  /** 事前顧客照会同意ステータス */
  refAgreeStatus?: string;
  /** 事前顧客照会同意確認フラグ */
  refAgreeConfirmFlg?: string;
  /** 情報照会回数 */
  refCnt?: string;
  /** クルー/Web識別区分 */
  webIdentificationType?: string;
  /** 照会ステータス */
  inquiryStatus?: string;
  /** 予約リスト用業務関連情報 */
  workInfoForGetScheduleListDto?: WorkInfoForGetSchedule[];
  hearingId: string | null;
  firstInsDate: string | null;
  /** LINEステータス */
  lineContactFlg?: string;
  /** 「ヒアリングQR」ステータス */
  hearingQr?: string;
}

/**
 * 予約リスト用業務関連情報
 */
export interface WorkInfoForGetSchedule {
  /** 業務コード */
  workCd: string;
  /** 内部業務名 */
  workNmIn: string;
  /** 業務詳細コード */
  workDtlCd: string;
  /** 業務詳細名 */
  workDtlNm: string;
  /** 登録順番 */
  entrySortNo: string;
}

/**
 * 事前準備.架電
 */
export interface BefCall {
  /** 事前架電（コード値） */
  befCallCode: string;
  /** 事前架電（名称） */
  befCallName: string;
  /** 事前架電単位集計 */
  befCallCount: number;
}

/**
 * 事前準備.LINE
 */
export interface LineContact {
  /** コード値 */
  lineContactCode?: string;
  /** 名称 */
  lineContactName: string;
  /** 単位集計 */
  lineContactCount: number;
}

/**
 * 事前準備.クルーヒアリング
 */
export interface Hearing {
  /** ヒアリング（コード値） */
  hearingCode: string;
  /** ヒアリング（名称） */
  hearingName: string;
  /** ヒアリング単位集計 */
  hearingCount: number;
}

/**
 * 事前準備.照会
 */
export interface RefAgreeStatus {
  /** 照会（コード値） */
  refAgreeStatusCode: string;
  /** 照会（名称） */
  refAgreeStatusName: string;
  /** 照会単位集計 */
  refAgreeStatusCount: number;
}

/**
 * 来店予約サマリ・一覧取得API
 * @param request リクエスト
 */
export async function getVisitSummaryApi(request: GetVisitSummaryApiRequest): Promise<GetVisitSummaryApiResponse> {
  const url = createApiRequestUrl('visit/summary');
  try {
    const response = await axios.post<GetVisitSummaryApiResponse>(url, request);
    const data = response.data;
    const status = response.status;

    switch (status) {
      case OK.status:
        return data;
      default:
        throw new Error(data.result.resultCd);
    }
  } catch (error) {
    return {
      visitReservationResultDto: {
        count: 0,
      },
      result: {
        jobDateTime: '',
        resultCd: '9999',
        resultMessageDtoList: [
          {
            resultMessageCd: '9999',
            resultMessage: `${API_EXCEPTION_MSG}  エラー内容:[${error}]`,
          },
        ],
      },
    };
  }
}
